/**
 * <predictive-search>
 * Wraps an <input type="search"> and a results dropdown.
 * Calls /search/suggest.json with debounce, supports keyboard nav.
 */
class PredictiveSearch extends HTMLElement {
  static DEBOUNCE_MS = 200;
  static BLUR_DELAY_MS = 200;
  static MIN_QUERY = 2;
  static LIMIT = 6;

  constructor() {
    super();
    this._timer = null;
    this._blurTimer = null;
    this._abort = null;
    this._results = [];
    this._activeIndex = -1;
    this._isOpen = false;
    this._lastQuery = '';

    this._onInput = this._onInput.bind(this);
    this._onFocus = this._onFocus.bind(this);
    this._onBlur = this._onBlur.bind(this);
    this._onKeydown = this._onKeydown.bind(this);
    this._onDocumentClick = this._onDocumentClick.bind(this);
    this._onResultsMousedown = this._onResultsMousedown.bind(this);
  }

  connectedCallback() {
    this.$input = this.querySelector('input[type="search"], input[name="q"]');
    this.$results = this.querySelector('[data-search-results]') || this._makeResultsNode();
    if (!this.$input) return;

    this.$input.setAttribute('autocomplete', 'off');
    this.$input.setAttribute('role', 'combobox');
    this.$input.setAttribute('aria-autocomplete', 'list');
    this.$input.setAttribute('aria-expanded', 'false');
    if (this.$results.id) {
      this.$input.setAttribute('aria-controls', this.$results.id);
    } else {
      this.$results.id = 'predictive-search-results';
      this.$input.setAttribute('aria-controls', this.$results.id);
    }
    this.$results.setAttribute('role', 'listbox');

    this.$input.addEventListener('input', this._onInput);
    this.$input.addEventListener('focus', this._onFocus);
    this.$input.addEventListener('blur', this._onBlur);
    this.$input.addEventListener('keydown', this._onKeydown);
    this.$results.addEventListener('mousedown', this._onResultsMousedown);
    document.addEventListener('click', this._onDocumentClick);
  }

  disconnectedCallback() {
    if (this._timer) clearTimeout(this._timer);
    if (this._blurTimer) clearTimeout(this._blurTimer);
    if (this._abort) this._abort.abort();
    if (this.$input) {
      this.$input.removeEventListener('input', this._onInput);
      this.$input.removeEventListener('focus', this._onFocus);
      this.$input.removeEventListener('blur', this._onBlur);
      this.$input.removeEventListener('keydown', this._onKeydown);
    }
    if (this.$results) {
      this.$results.removeEventListener('mousedown', this._onResultsMousedown);
    }
    document.removeEventListener('click', this._onDocumentClick);
  }

  _makeResultsNode() {
    const div = document.createElement('div');
    div.className = 'predictive-search__results';
    div.setAttribute('data-search-results', '');
    div.hidden = true;
    this.appendChild(div);
    return div;
  }

  // ---------- Events ----------
  _onInput() {
    const q = (this.$input.value || '').trim();
    if (this._timer) clearTimeout(this._timer);
    if (q.length < PredictiveSearch.MIN_QUERY) {
      this._results = [];
      this._activeIndex = -1;
      this._renderClosed();
      return;
    }
    this._timer = setTimeout(() => this._fetch(q), PredictiveSearch.DEBOUNCE_MS);
  }

  _onFocus() {
    if (this._blurTimer) {
      clearTimeout(this._blurTimer);
      this._blurTimer = null;
    }
    if ((this.$input.value || '').trim().length >= PredictiveSearch.MIN_QUERY && this._results.length) {
      this._open();
    }
  }

  _onBlur() {
    // Delay so a click on a result registers first.
    this._blurTimer = setTimeout(() => this._close(), PredictiveSearch.BLUR_DELAY_MS);
  }

  _onDocumentClick(e) {
    if (this.contains(e.target)) return;
    this._close();
  }

  _onResultsMousedown(e) {
    // Prevent the input's blur before a click is registered on a link.
    if (e.target.closest('a')) {
      // allow default navigation
      return;
    }
    e.preventDefault();
  }

  _onKeydown(e) {
    if (!this._isOpen) {
      if (e.key === 'ArrowDown' && this._results.length) {
        e.preventDefault();
        this._open();
        this._setActive(0);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        this._setActive(Math.min(this._results.length - 1, this._activeIndex + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        this._setActive(Math.max(-1, this._activeIndex - 1));
        break;
      case 'Enter': {
        if (this._activeIndex >= 0 && this._results[this._activeIndex]) {
          e.preventDefault();
          const item = this._results[this._activeIndex];
          if (item && item.url) window.location.href = item.url;
        }
        // Otherwise, let the form submit naturally.
        break;
      }
      case 'Escape':
        e.preventDefault();
        this._close();
        this.$input.blur();
        break;
      default:
        break;
    }
  }

  // ---------- Networking ----------
  async _fetch(q) {
    if (this._abort) this._abort.abort();
    this._abort = new AbortController();
    this._lastQuery = q;
    this._renderLoading(q);
    this._open();

    const params = new URLSearchParams();
    params.set('q', q);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', String(PredictiveSearch.LIMIT));
    params.set('resources[options][unavailable_products]', 'last');

    try {
      const res = await fetch(`/search/suggest.json?${params.toString()}`, {
        headers: { 'Accept': 'application/json' },
        signal: this._abort.signal
      });
      if (!res.ok) throw new Error('Suggest fetch failed');
      const data = await res.json();
      const products = (data && data.resources && data.resources.results && data.resources.results.products) || [];
      this._results = products.slice(0, PredictiveSearch.LIMIT).map((p) => ({
        id: p.id,
        title: p.title,
        url: p.url,
        image: (p.image && (p.image.url || p.image)) || p.featured_image || '',
        price: p.price || ''
      }));
      this._activeIndex = -1;
      this._renderResults(q);
    } catch (err) {
      if (err && err.name === 'AbortError') return;
      this._renderError();
    }
  }

  // ---------- Render ----------
  _renderLoading(q) {
    if (!this.$results) return;
    this.$results.innerHTML = `<p class="predictive-search__loading">Buscando...</p>`;
  }

  _renderError() {
    if (!this.$results) return;
    this.$results.innerHTML = `<p class="predictive-search__error" role="alert">Error al buscar. Intenta de nuevo.</p>`;
  }

  _renderResults(q) {
    if (!this.$results) return;
    const safeQ = this._escape(q);
    if (!this._results.length) {
      this.$results.innerHTML = `
        <p class="predictive-search__empty">No hay resultados para "${safeQ}"</p>
        <a class="predictive-search__all" href="/search?q=${encodeURIComponent(q)}">Ver todos los resultados &rarr;</a>
      `;
      return;
    }

    const items = this._results.map((p, i) => {
      const url = this._escape(p.url || '#');
      const title = this._escape(p.title || '');
      const price = this._formatPrice(p.price);
      const img = p.image ? `<img src="${this._escape(p.image)}" alt="${title}" loading="lazy" width="48" height="48">` : '';
      return `
        <li role="option" id="ps-opt-${i}" data-index="${i}" class="predictive-search__item">
          <a href="${url}" class="predictive-search__link">
            <span class="predictive-search__media">${img}</span>
            <span class="predictive-search__info">
              <span class="predictive-search__title">${title}</span>
              ${price ? `<span class="predictive-search__price">${price}</span>` : ''}
            </span>
          </a>
        </li>
      `;
    }).join('');

    this.$results.innerHTML = `
      <ul class="predictive-search__list" role="presentation">${items}</ul>
      <a class="predictive-search__all" href="/search?q=${encodeURIComponent(q)}">Ver todos los resultados &rarr;</a>
    `;
  }

  _renderClosed() {
    if (this.$results) {
      this.$results.innerHTML = '';
      this.$results.hidden = true;
    }
    this._isOpen = false;
    if (this.$input) {
      this.$input.setAttribute('aria-expanded', 'false');
      this.$input.removeAttribute('aria-activedescendant');
    }
  }

  _open() {
    if (!this.$results) return;
    this.$results.hidden = false;
    this._isOpen = true;
    if (this.$input) this.$input.setAttribute('aria-expanded', 'true');
  }

  _close() {
    this._renderClosed();
    this._activeIndex = -1;
  }

  _setActive(index) {
    this._activeIndex = index;
    if (!this.$results) return;
    const items = this.$results.querySelectorAll('[role="option"]');
    items.forEach((it, i) => {
      if (i === index) {
        it.setAttribute('aria-selected', 'true');
        it.classList.add('is-active');
        if (this.$input) this.$input.setAttribute('aria-activedescendant', `ps-opt-${i}`);
        // Scroll into view if needed.
        if (typeof it.scrollIntoView === 'function') {
          it.scrollIntoView({ block: 'nearest' });
        }
      } else {
        it.removeAttribute('aria-selected');
        it.classList.remove('is-active');
      }
    });
    if (index < 0 && this.$input) this.$input.removeAttribute('aria-activedescendant');
  }

  // ---------- Helpers ----------
  _formatPrice(price) {
    if (price === null || price === undefined || price === '') return '';
    // Shopify suggest returns prices as decimal strings ("19.95") for products.
    const n = Number(price);
    if (Number.isFinite(n)) {
      return `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return this._escape(String(price));
  }

  _escape(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
}

customElements.define('predictive-search', PredictiveSearch);
